import { ReactNode } from "react";
import { useT } from "@hooks";
import KpiInfoTooltip, { formatPeriodRange } from "@components/shared/KpiInfoTooltip";
import KpiComparisonTooltip from "@components/shared/KpiComparisonTooltip";
import { toPtBrNumberFormat } from "@utils/format";
import type { KpiComparison, KpiComparisonPeriod } from "@types";
import { resolveKpiComparison } from "@utils/kpiComparison";
import KpiCard, { KpiCardDelta, KpiCardIcon } from "./KpiCard";

interface KPIData {
  balance: number;
  totalExpenses: number;
  totalRevenue: number;
  profitMargin: number;
  balanceKpi?: KpiComparison | null;
  totalExpensesKpi?: KpiComparison | null;
  totalRevenueKpi?: KpiComparison | null;
  profitMarginKpi?: KpiComparison | null;
  currentPeriod?: KpiComparisonPeriod | null;
  previousPeriod?: KpiComparisonPeriod | null;
}

interface KPICardsProps {
  kpi?: KPIData | null;
  previousKpi?: KPIData | null;
  startDate?: string;
  endDate?: string;
}

const formatCurrency = (value?: number) => (value ?? 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 });

const formatComparisonCurrency = (value: number) => `R$ ${formatCurrency(value)}`;

const formatComparisonPercent = (value: number) => `${formatProfitMargin(value)}%`;

const formatProfitMargin = (margin: number): string => {
  // Backend já calcula: (lucroOperacional / receitaBruta) * 100
  return toPtBrNumberFormat(margin % 1 === 0 ? margin.toFixed(0) : margin.toFixed(2));
};

// Variação percentual em relação ao mês anterior, calculada no cliente
// (o backend ainda não expõe esse comparativo diretamente).
const calculateDelta = (current: number, previous?: number): number | null => {
  if (previous === undefined || previous === null || previous === 0) return null;
  return ((current - previous) / Math.abs(previous)) * 100;
};

interface DashboardKpiCardProps {
  label: string;
  tooltip: ReactNode;
  value: string;
  symbol?: string;
  deltaPercentage: number | null;
  comparison?: KpiComparison | null;
  formatComparisonValue: (value: number) => string;
}

const DashboardKpiCard = ({
  label,
  tooltip,
  value,
  symbol,
  deltaPercentage,
  comparison,
  formatComparisonValue,
}: DashboardKpiCardProps) => {
  const t = useT();

  return (
    <KpiCard
      label={
        <>
          {label}
          {tooltip}
        </>
      }
      value={value}
      icon={<KpiCardIcon symbol={symbol} />}
      contentGapClassName="gap-15"
      footer={
        <KpiComparisonTooltip comparison={comparison} formatValue={formatComparisonValue}>
          <KpiCardDelta
            deltaPercentage={comparison ? comparison.variationPercent : deltaPercentage}
            vsLabel={t("kpi.vsLastMonth")}
            roundPercentage
          />
        </KpiComparisonTooltip>
      }
    />
  );
};

export default function KPICards({ kpi, previousKpi, startDate, endDate }: Readonly<KPICardsProps>) {
  const t = useT();
  const periodRange = formatPeriodRange(startDate, endDate);
  const kpiPeriods = { currentPeriod: kpi?.currentPeriod, previousPeriod: kpi?.previousPeriod };
  const safeKpi = kpi ?? {
    balance: 0,
    totalExpenses: 0,
    totalRevenue: 0,
    profitMargin: 0,
  };

  // Usa o profitMargin calculado pelo backend (lucroOperacional / receitaBruta)
  const correctedProfitMargin = formatProfitMargin(safeKpi.profitMargin);

  return (
    <div className="display-grid grid-cols-1 grid-cols-2-sm grid-cols-4-lg gap-10 margin-bottom-20">
      <DashboardKpiCard
        label={t("dashboard.kpi.balance")}
        tooltip={
          <KpiInfoTooltip
            title={t("dashboard.kpi.balance")}
            description={t("dashboard.kpi.balance.tooltip")}
            lines={[
              `${t("dashboard.kpi.totalRevenue")} - ${t("dashboard.kpi.totalExpenses")}`,
              `R$ ${formatCurrency(safeKpi.totalRevenue)} - R$ ${formatCurrency(safeKpi.totalExpenses)} = R$ ${formatCurrency(safeKpi.balance)}`,
            ]}
            periodRange={periodRange}
          />
        }
        value={formatCurrency(safeKpi.balance)}
        deltaPercentage={calculateDelta(safeKpi.balance, previousKpi?.balance)}
        comparison={resolveKpiComparison(kpi?.balanceKpi, kpiPeriods)}
        formatComparisonValue={formatComparisonCurrency}
      />

      <DashboardKpiCard
        label={t("dashboard.kpi.totalExpenses")}
        tooltip={
          <KpiInfoTooltip
            title={t("dashboard.kpi.totalExpenses")}
            description={t("dashboard.kpi.totalExpenses.tooltip")}
            lines={[`R$ ${formatCurrency(safeKpi.totalExpenses)}`]}
            periodRange={periodRange}
          />
        }
        value={formatCurrency(safeKpi.totalExpenses)}
        deltaPercentage={calculateDelta(safeKpi.totalExpenses, previousKpi?.totalExpenses)}
        comparison={resolveKpiComparison(kpi?.totalExpensesKpi, kpiPeriods)}
        formatComparisonValue={formatComparisonCurrency}
      />

      <DashboardKpiCard
        label={t("dashboard.kpi.totalRevenue")}
        tooltip={
          <KpiInfoTooltip
            title={t("dashboard.kpi.totalRevenue")}
            description={t("dashboard.kpi.totalRevenue.tooltip")}
            lines={[`R$ ${formatCurrency(safeKpi.totalRevenue)}`]}
            periodRange={periodRange}
          />
        }
        value={formatCurrency(safeKpi.totalRevenue)}
        deltaPercentage={calculateDelta(safeKpi.totalRevenue, previousKpi?.totalRevenue)}
        comparison={resolveKpiComparison(kpi?.totalRevenueKpi, kpiPeriods)}
        formatComparisonValue={formatComparisonCurrency}
      />

      <DashboardKpiCard
        label={t("dashboard.kpi.profitMargin")}
        tooltip={
          <KpiInfoTooltip
            title={t("dashboard.kpi.profitMargin")}
            description={t("dashboard.kpi.profitMargin.tooltip")}
            placement="bottom-end"
            lines={[
              `${t("dashboard.kpi.balance")} ÷ ${t("dashboard.kpi.totalRevenue")} X 100`,
              `R$ ${formatCurrency(safeKpi.balance)} ÷ R$ ${formatCurrency(safeKpi.totalRevenue)} X 100 = ${correctedProfitMargin}%`,
            ]}
            periodRange={periodRange}
          />
        }
        value={`${correctedProfitMargin}%`}
        symbol="%"
        deltaPercentage={calculateDelta(safeKpi.profitMargin, previousKpi?.profitMargin)}
        comparison={resolveKpiComparison(kpi?.profitMarginKpi, kpiPeriods)}
        formatComparisonValue={formatComparisonPercent}
      />
    </div>
  );
}
